// Lead finder: describe the ideal customer -> editable filter chips -> search Apollo or Google Maps (Apify)
// on the server -> tick rows -> "Add as leads". Nothing is saved before that click.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge, Loader2, Search, Sparkles, UserPlus, X } from "lucide-react";
import {
  CanAccess,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState, type KeyboardEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../../providers/types";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import { runAiJson } from "../ai";
import { LeadAgent } from "./LeadAgent";
import {
  buildFitPrompt,
  criteriaOf,
  fitData,
  FitAnswerSchema,
  overallFit,
  reasonsFrom,
} from "../fit";

import {
  addLeads,
  AiFiltersSchema,
  buildFiltersPrompt,
  capMessage,
  EMPLOYEE_RANGES,
  ENGINE_KEY,
  LEAD_ENGINES,
  type Lead,
  type LeadEngine,
  type LeadFilters,
  type SearchAnswer,
  DEFAULT_DAILY_CAP,
  profileText,
  runLeadSearch,
  useEngineStatus,
} from "./leads";

const EMPTY: LeadFilters = {
  keywords: [],
  job_titles: [],
  locations: [],
  employee_ranges: [],
};

type Fit = { fit_score: number; fit_match: 1 | 2 | 3 } & Record<
  string,
  unknown
>;

export const LeadFinder = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();
  const cap = config.lead_engine_daily_cap ?? DEFAULT_DAILY_CAP;
  const { data: status = [] } = useEngineStatus();
  const { data: usage = {} } = useQuery({
    queryKey: ["lead_engines", "usage"],
    queryFn: () => dataProvider.engineUsageToday(),
  });

  const [description, setDescription] = useState("");
  const [filters, setFilters] = useState<LeadFilters>(EMPTY);
  const [engine, setEngine] = useState<LeadEngine>("apollo_companies");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<Lead[]>([]);
  const [excluded, setExcluded] = useState(0);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [fits, setFits] = useState<Record<string, Fit>>({});

  const keyName = ENGINE_KEY[engine];
  const hasKey = status.find((s) => s.engine === keyName)?.has_key ?? false;
  const profile = config.ideal_customers?.[0];

  const fail = (error: unknown) =>
    notify((error as Error).message, { type: "error" });

  const suggest = async () => {
    setBusy("filters");
    try {
      setFilters(
        await runAiJson(
          "lead_filters",
          buildFiltersPrompt(description),
          AiFiltersSchema,
        ),
      );
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const show = (answer: SearchAnswer) => {
    setResults(answer.results ?? []);
    setExcluded(answer.excluded ?? 0);
    setTicked(new Set());
  };

  const search = async (nextPage = 1) => {
    setBusy("search");
    try {
      const answer = await runLeadSearch(
        dataProvider,
        engine,
        filters,
        nextPage,
        translate("resources.leads.still_running"),
      );
      setPage(nextPage);
      show(answer);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
      queryClient.invalidateQueries({ queryKey: ["lead_engines", "usage"] });
    }
  };

  const score = async (lead: Lead) => {
    if (!profile) return;
    setBusy(lead.ref);
    try {
      const criteria = criteriaOf(profile, "companies");
      const record = fitData("companies", {
        ...lead.company,
        description: lead.company.industry,
        size: lead.company.employees,
      });
      const answer = await runAiJson(
        "score_fit",
        buildFitPrompt(profile, criteria, "companies", record),
        FitAnswerSchema,
      );
      const fit_reasons = reasonsFrom(criteria, answer);
      const overall = overallFit(fit_reasons);
      if (!overall) throw new Error(translate("resources.fit.failed"));
      setFits({
        ...fits,
        [lead.ref]: {
          ...overall,
          fit_reasons,
          fit_profile: profile.name,
          fit_scored_at: new Date().toISOString(),
        } as Fit,
      });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const add = async () => {
    const chosen = results.filter((lead) => ticked.has(lead.ref));
    setBusy("add");
    try {
      const { added, skipped } = await addLeads(chosen, dataProvider, {
        salesId: identity?.id,
        sectors: config.companySectors,
        fits,
      });
      notify("resources.leads.added", {
        type: "success",
        messageArgs: { smart_count: added, skipped: skipped.length },
      });
      setResults(results.filter((lead) => !ticked.has(lead.ref)));
      setTicked(new Set());
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const toggle = (ref: string) => {
    const next = new Set(ticked);
    if (next.has(ref)) next.delete(ref);
    else next.add(ref);
    setTicked(next);
  };

  return (
    <div className="mt-4 flex flex-col gap-4 max-w-screen-xl">
      <Card>
        <CardContent className="flex flex-col gap-3">
          <h1 className="text-2xl font-semibold">
            {translate("resources.leads.title")}
          </h1>
          <Textarea
            aria-label={translate("resources.leads.describe")}
            placeholder={translate("resources.leads.describe_placeholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!description.trim() || busy !== null}
              onClick={suggest}
            >
              {busy === "filters" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Sparkles />
              )}
              {translate("resources.leads.suggest")}
            </Button>
            {profile && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDescription(profileText(profile))}
              >
                {translate("resources.leads.use_profile", {
                  name: profile.name,
                })}
              </Button>
            )}
          </div>

          <ChipRow
            label="resources.leads.filters.keywords"
            values={filters.keywords}
            max={5}
            onChange={(keywords) => setFilters({ ...filters, keywords })}
          />
          <ChipRow
            label="resources.leads.filters.job_titles"
            values={filters.job_titles}
            max={5}
            onChange={(job_titles) => setFilters({ ...filters, job_titles })}
          />
          <ChipRow
            label="resources.leads.filters.locations"
            values={filters.locations}
            max={3}
            onChange={(locations) => setFilters({ ...filters, locations })}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground w-32">
              {translate("resources.leads.filters.employees")}
            </span>
            {EMPLOYEE_RANGES.map((range) => {
              const on = filters.employee_ranges.includes(range);
              return (
                <Button
                  key={range}
                  type="button"
                  size="sm"
                  variant={on ? "default" : "outline"}
                  aria-pressed={on}
                  onClick={() =>
                    setFilters({
                      ...filters,
                      employee_ranges: on
                        ? filters.employee_ranges.filter((r) => r !== range)
                        : [...filters.employee_ranges, range],
                    })
                  }
                >
                  {range.replace(",", "–")}
                </Button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              className="h-9 rounded-md border bg-transparent px-2 text-sm"
              aria-label={translate("resources.leads.engine")}
              value={engine}
              onChange={(e) => setEngine(e.target.value as LeadEngine)}
            >
              {LEAD_ENGINES.map((e) => (
                <option key={e} value={e}>
                  {translate(`resources.leads.engines.${e}`)}
                </option>
              ))}
            </select>
            <Button
              type="button"
              disabled={busy !== null || !hasKey}
              onClick={() => search(1)}
            >
              {busy === "search" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Search />
              )}
              {translate(
                busy === "search" && engine === "apify_maps"
                  ? "resources.leads.searching_maps"
                  : "resources.leads.search",
              )}
            </Button>
            <span className="text-sm text-muted-foreground">
              {capMessage(usage[keyName] ?? 0, cap)}
            </span>
            {!hasKey && (
              <span className="text-sm text-destructive">
                {translate("resources.leads.no_key")}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {(results.length > 0 || excluded > 0) && (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-muted-foreground flex-1">
                {translate("resources.leads.excluded", {
                  smart_count: excluded,
                })}
              </span>
              {engine !== "apify_maps" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy !== null}
                  onClick={() => search(page + 1)}
                >
                  {translate("resources.leads.next_page")}
                </Button>
              )}
              <Button
                type="button"
                disabled={!ticked.size || busy !== null}
                onClick={add}
              >
                {busy === "add" ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <UserPlus />
                )}
                {translate("resources.leads.add", { smart_count: ticked.size })}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="p-2 w-8" />
                    <th className="p-2">
                      {translate("resources.leads.columns.company")}
                    </th>
                    <th className="p-2">
                      {translate("resources.leads.columns.contact")}
                    </th>
                    <th className="p-2">
                      {translate("resources.leads.columns.where")}
                    </th>
                    <th className="p-2">
                      {translate("resources.leads.columns.fit")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((lead) => {
                    const fit = fits[lead.ref];
                    return (
                      <tr key={lead.ref} className="border-t align-top">
                        <td className="p-2">
                          <Checkbox
                            aria-label={lead.company.name}
                            checked={ticked.has(lead.ref)}
                            onCheckedChange={() => toggle(lead.ref)}
                          />
                        </td>
                        <td className="p-2">
                          <div className="font-medium">{lead.company.name}</div>
                          <div className="text-muted-foreground">
                            {[
                              lead.company.domain,
                              lead.company.industry,
                              lead.company.phone,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </td>
                        <td className="p-2">
                          {lead.contact && (
                            <>
                              <div>
                                {lead.contact.first_name}{" "}
                                {lead.contact.last_name}
                              </div>
                              <div className="text-muted-foreground">
                                {lead.contact.title}
                              </div>
                            </>
                          )}
                        </td>
                        <td className="p-2">
                          {[lead.company.city, lead.company.country]
                            .filter(Boolean)
                            .join(", ")}
                        </td>
                        <td className="p-2">
                          {fit ? (
                            <Badge variant="secondary">
                              <Gauge />
                              {translate("resources.fit.badge", {
                                score: fit.fit_score,
                              })}
                            </Badge>
                          ) : profile ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={busy !== null}
                              onClick={() => score(lead)}
                            >
                              {busy === lead.ref && (
                                <Loader2 className="animate-spin" />
                              )}
                              {translate("resources.fit.action")}
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

LeadFinder.path = "/leads/search";

/** The route: admin and sales (canAccess "leads"); delivery gets nothing */
export const LeadFinderPage = () => (
  <CanAccess resource="leads" action="list">
    <div className="mt-4 flex flex-col gap-4 max-w-screen-xl">
      <LeadAgent />
    </div>
    <LeadFinder />
  </CanAccess>
);

/** Editable chips: type and press Enter to add, x to remove */
const ChipRow = ({
  label,
  values,
  max,
  onChange,
}: {
  label: string;
  values: string[];
  max: number;
  onChange: (values: string[]) => void;
}) => {
  const translate = useTranslate();
  const [draft, setDraft] = useState("");
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const value = draft.trim().slice(0, 60);
    if (value && !values.includes(value) && values.length < max)
      onChange([...values, value]);
    setDraft("");
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground w-32">
        {translate(label)}
      </span>
      {values.map((value) => (
        <Badge key={value} variant="secondary" className="gap-1">
          {value}
          <button
            type="button"
            aria-label={translate("ra.action.remove")}
            onClick={() => onChange(values.filter((v) => v !== value))}
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      {values.length < max && (
        <Input
          className="h-8 w-48"
          aria-label={translate(label)}
          placeholder={translate("resources.leads.add_chip")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
        />
      )}
    </div>
  );
};
